import { AlertOctagon, AlertTriangle, CheckCircle2, Package, UserMinus, Gem } from 'lucide-react'
import type { RedemptionSummary } from '@/lib/api-client'

function Card({
  icon: Icon,
  label,
  value,
  hint,
  accent,
  active,
  onClick,
}: {
  icon: typeof Package
  label: string
  value: React.ReactNode
  hint?: string
  accent?: string
  active?: boolean
  onClick?: () => void
}) {
  const baseRing = active ? 'border-foreground/40 ring-1 ring-foreground/10' : 'border-border'
  const interactive = onClick ? 'cursor-pointer hover:border-foreground/30 transition-colors' : ''
  return (
    <div
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`rounded-xl border ${baseRing} ${interactive} bg-card p-4`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-text-muted">{label}</span>
        <Icon className="w-4 h-4 text-text-muted" />
      </div>
      <div className={`text-2xl font-bold ${accent ?? 'text-text-dark'}`}>{value}</div>
      {hint && <div className="text-xs text-text-muted mt-1">{hint}</div>}
    </div>
  )
}

interface Props {
  data: RedemptionSummary | undefined
  isLoading: boolean
  activeFilter?: 'all' | 'critical' | 'warning' | 'active' | 'churn'
  onFilterChange?: (filter: 'all' | 'critical' | 'warning' | 'active' | 'churn') => void
}

export function RedemptionSummaryCards({ data, isLoading, activeFilter = 'all', onFilterChange }: Props) {
  if (isLoading || !data) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4 h-[100px] animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4 h-[100px] animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  const fmt = (n: number) => n.toLocaleString()

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-text-muted mb-2">Recovery</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card
            icon={AlertOctagon}
            label="Critical"
            value={fmt(data.critical)}
            hint="outstanding ≥ 3 · 60-90 days gone"
            accent="text-destructive"
            active={activeFilter === 'critical'}
            onClick={() => onFilterChange?.(activeFilter === 'critical' ? 'all' : 'critical')}
          />
          <Card
            icon={AlertTriangle}
            label="Warning"
            value={fmt(data.warning)}
            hint="outstanding ≥ 1 · 30+ days gone"
            accent="text-warning"
            active={activeFilter === 'warning'}
            onClick={() => onFilterChange?.(activeFilter === 'warning' ? 'all' : 'warning')}
          />
          <Card
            icon={CheckCircle2}
            label="Active"
            value={fmt(data.active)}
            hint="outstanding ≥ 1 · visited last 30 days"
            accent="text-success"
            active={activeFilter === 'active'}
            onClick={() => onFilterChange?.(activeFilter === 'active' ? 'all' : 'active')}
          />
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-text-muted mb-2">Sales & Ops Insight</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card
            icon={Package}
            label="Pending Sessions"
            value={fmt(data.pendingSessions)}
            hint={`across ${fmt(data.totalOutstanding)} patients`}
          />
          <Card
            icon={UserMinus}
            label="Churn Risk"
            value={fmt(data.churn)}
            hint="outstanding ≥ 3 · 90+ days gone"
            accent="text-text-muted"
            active={activeFilter === 'churn'}
            onClick={() => onFilterChange?.(activeFilter === 'churn' ? 'all' : 'churn')}
          />
          <Card
            icon={Gem}
            label="Upsell Ready"
            value="—"
            hint="completed packages (next phase)"
            accent="text-text-muted"
          />
        </div>
      </div>
    </div>
  )
}
